import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Pause,
  Play,
  Search,
} from "lucide-react";
import type { FileData } from "../data";
import FileRow from "./FileRow";
import Waveform from "./Waveform";
import {
  getFileUrl,
  groupFiles,
  isAudioFormat,
  pickDefaultAudioFile,
} from "../lib/media";

type AdminProject = {
  id: string;
  title: string;
  sourceFolder: string;
  fileCount: number;
  files: FileData[];
};

type AdminProjectsResult = {
  lastSyncedAt: number | null;
  projects: AdminProject[];
};

export type AdminLibraryKey = "beats" | "sounds";

const LIBRARY_CONFIG: Record<AdminLibraryKey, {
  title: string;
  description: string;
  searchLabel: string;
  searchPlaceholder: string;
  fileSingular: string;
  filePlural: string;
  emptySearch: string;
  emptyFolder: string;
  setupTitle: string;
  setupCopy: string;
}> = {
  beats: {
    title: "Beats",
    description: "Private instrumental library synced from the top-level Google Drive Beats folder.",
    searchLabel: "Search beats",
    searchPlaceholder: "Search beats or files",
    fileSingular: "instrumental",
    filePlural: "instrumentals",
    emptySearch: "No beats or files match",
    emptyFolder: "This Drive folder is ready for instrumentals.",
    setupTitle: "Create your Beats folder",
    setupCopy: "Add a top-level Google Drive folder named Beats, then place instrumentals directly inside it or organise them in subfolders. The portal will sync it automatically.",
  },
  sounds: {
    title: "Sounds",
    description: "Private sound library recursively synced from the top-level Google Drive Sounds folder.",
    searchLabel: "Search sounds",
    searchPlaceholder: "Search sounds or files",
    fileSingular: "file",
    filePlural: "files",
    emptySearch: "No sounds or files match",
    emptyFolder: "This Drive folder is ready for sounds.",
    setupTitle: "Create your Sounds folder",
    setupCopy: "Add a top-level Google Drive folder named Sounds, then organise drum kits, beat packs, sample packs, or other sound files in as many nested folders as you need. The portal will sync it automatically.",
  },
};

function formatLastSync(timestamp: number | null): string {
  if (!timestamp) return "Sync pending";
  return `Synced ${new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
  }).format(new Date(timestamp))}`;
}

export default function AdminProjects({ libraryKey = "beats" }: { libraryKey?: AdminLibraryKey }) {
  const config = LIBRARY_CONFIG[libraryKey];
  const result = useQuery(api.adminProjects.getAdminProjects, { libraryKey });
  const audioRef = useRef<HTMLAudioElement>(null);
  const [expandedProjects, setExpandedProjects] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFileId, setActiveFileId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const library = (result ?? { lastSyncedAt: null, projects: [] }) as AdminProjectsResult;
  const projects = library.projects;
  const filteredProjects = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return projects;
    return projects.filter((project) => {
      if (project.title.toLowerCase().includes(query)) return true;
      if (project.sourceFolder.toLowerCase().includes(query)) return true;
      return project.files.some((file) => file.title.toLowerCase().includes(query));
    });
  }, [projects, searchQuery]);
  const activeFile = useMemo(
    () =>
      projects
        .flatMap((project) => project.files)
        .find((file) => file.id === activeFileId) ?? null,
    [activeFileId, projects],
  );
  const activeUrl = activeFile ? getFileUrl(activeFile) : undefined;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrentTime(audio.currentTime);
    const onMeta = () => setDuration(audio.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, [activeUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !activeUrl) return;
    audio.load();
    setCurrentTime(0);
  }, [activeUrl]);

  function playFile(file: FileData) {
    const url = getFileUrl(file);
    if (!url || file.access !== "available" || !isAudioFormat(file.format, file.title)) {
      return;
    }
    const same = file.id === activeFileId;
    setActiveFileId(file.id);
    window.requestAnimationFrame(() => {
      const audio = audioRef.current;
      if (!audio) return;
      if (same && !audio.paused) {
        audio.pause();
        return;
      }
      void audio.play().catch((error) => {
        console.error("Audio play failed", error);
        setIsPlaying(false);
      });
    });
  }

  function toggleProject(projectId: string) {
    setExpandedProjects((current) =>
      current.includes(projectId)
        ? current.filter((id) => id !== projectId)
        : [...current, projectId],
    );
  }

  function expandAll() {
    setExpandedProjects((current) => [
      ...new Set([...current, ...filteredProjects.map((project) => project.id)]),
    ]);
  }

  function collapseAll() {
    const filteredIds = new Set(filteredProjects.map((project) => project.id));
    setExpandedProjects((current) => current.filter((id) => !filteredIds.has(id)));
  }

  if (result === undefined) {
    return (
      <div className="space-stack">
        <section className="hero-panel">
          <div>
            <p className="eyebrow">Admin library</p>
            <h1>{config.title}</h1>
            <p>Loading your {config.title.toLowerCase()} library…</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-stack">
      <audio ref={audioRef} preload="metadata" src={activeUrl} />

      <section className="hero-panel">
        <div>
          <p className="eyebrow">Admin library</p>
          <h1>{config.title}</h1>
          <p>{config.description}</p>
        </div>
        <div className="admin-library-hero-meta">
          <span className="admin-badge">Admin only</span>
          <span className="soft-status">{formatLastSync(library.lastSyncedAt)}</span>
        </div>
      </section>

      <section className="admin-library-toolbar" aria-label={`${config.title} library controls`}>
        <label className="search-field admin-library-search">
          <Search className="h-4 w-4" />
          <span className="sr-only">{config.searchLabel}</span>
          <input
            aria-label={config.searchLabel}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={config.searchPlaceholder}
            type="search"
            value={searchQuery}
          />
        </label>
        <div className="admin-library-tool-actions">
          <button className="soft-status admin-library-tool" onClick={expandAll} type="button">
            <ChevronsDownUp className="h-4 w-4" />
            Expand all
          </button>
          <button className="soft-status admin-library-tool" onClick={collapseAll} type="button">
            <ChevronsUpDown className="h-4 w-4" />
            Collapse all
          </button>
        </div>
        <p className="admin-library-result-count">
          {filteredProjects.length === projects.length
            ? `${projects.length} folders`
            : `${filteredProjects.length} of ${projects.length} folders`}
        </p>
      </section>

      {projects.length > 0 ? (
        filteredProjects.length > 0 ? (
          <div className="project-file-list projects-list-simple">
            {filteredProjects.map((project) => {
            const expanded = expandedProjects.includes(project.id);
            const listId = "admin-project-files-" + project.id;
            const grouped = groupFiles(project.files);
            const defaultAudio = pickDefaultAudioFile(project.files);
            const projectActiveFile =
              activeFile && project.files.some((file) => file.id === activeFile.id)
                ? activeFile
                : defaultAudio;
            const projectActiveUrl = projectActiveFile
              ? getFileUrl(projectActiveFile)
              : undefined;
            const projectIsPlaying =
              isPlaying &&
              !!activeFile &&
              project.files.some((file) => file.id === activeFile.id);

            return (
              <div
                className={"project-file-group " + (expanded ? "expanded" : "")}
                key={project.id}
              >
                <div className={"project-file-heading " + (expanded ? "open" : "")}>
                  <button
                    aria-controls={listId}
                    aria-expanded={expanded}
                    className="project-file-heading-main"
                    onClick={() => toggleProject(project.id)}
                    type="button"
                  >
                    <div>
                      <strong>{project.title}</strong>
                      <small>
                        {project.fileCount}{" "}
                        {project.fileCount === 1 ? config.fileSingular : config.filePlural}
                      </small>
                    </div>
                    <ChevronDown
                      className={"h-4 w-4 chevron " + (expanded ? "rotate-180" : "")}
                    />
                  </button>
                  <span className="project-heading-actions">
                    <span className="soft-status">Drive folder</span>
                  </span>
                </div>

                {expanded ? (
                  <div className="project-expand-body" id={listId}>
                    {projectActiveUrl ? (
                      <div className="project-expand-player">
                        <div className="project-expand-player-head">
                          <div>
                            <p className="eyebrow">Preview</p>
                            <strong>{projectActiveFile?.title}</strong>
                          </div>
                          <button
                            aria-label={`${projectIsPlaying ? "Pause" : "Play"} ${projectActiveFile?.title ?? "instrumental"}`}
                            className={`soundcloud-play-button soundcloud-play-button-compact ${projectIsPlaying ? "playing" : ""}`}
                            onClick={() => {
                              if (projectActiveFile) playFile(projectActiveFile);
                            }}
                            title={projectIsPlaying ? "Pause track" : "Play track"}
                            type="button"
                          >
                            {projectIsPlaying ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                        <Waveform
                          audioUrl={
                            activeFile && project.files.some((file) => file.id === activeFile.id)
                              ? activeUrl
                              : projectActiveUrl
                          }
                          currentTime={
                            activeFile && project.files.some((file) => file.id === activeFile.id)
                              ? currentTime
                              : 0
                          }
                          duration={
                            activeFile && project.files.some((file) => file.id === activeFile.id)
                              ? duration
                              : 0
                          }
                          height={96}
                          isPlaying={projectIsPlaying}
                          onSeek={(time) => {
                            if (audioRef.current) {
                              audioRef.current.currentTime = time;
                              setCurrentTime(time);
                            }
                          }}
                        />
                      </div>
                    ) : null}

                    {grouped.length > 0 ? (
                      <div className="file-group-stack compact">
                        {grouped.map(({ group, files }) => (
                          <div className="file-group" key={group.id}>
                            <div className="file-group-heading static">
                              <div>
                                <strong>{group.label}</strong>
                                <small>
                                  {files.length} {files.length === 1 ? "file" : "files"}
                                </small>
                              </div>
                            </div>
                            <div className="file-group-list">
                              {files.map((file) => (
                                <div className="file-row compact-file-row" key={file.id}>
                                  <div className="file-title-line">
                                    <strong>{file.title}</strong>
                                  </div>
                                  <FileRow
                                    canDelete={false}
                                    canDownload
                                    compact
                                    file={file}
                                    isActive={activeFileId === file.id}
                                    isAdmin
                                    isPlaying={isPlaying && activeFileId === file.id}
                                    onDelete={async () => undefined}
                                    onPlay={playFile}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="empty-state">
                        {config.emptyFolder}
                      </p>
                    )}

                    <p className="project-source-path">{project.sourceFolder}</p>
                    <div className="project-expand-footer">
                      <span className="soft-status">Private admin library</span>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                ) : null}
              </div>
            );
            })}
          </div>
        ) : (
          <section className="content-panel">
            <p className="empty-state">{config.emptySearch} “{searchQuery}”.</p>
          </section>
        )
      ) : (
        <section className="content-panel">
          <p className="eyebrow">Google Drive setup</p>
          <h2>{config.setupTitle}</h2>
          <p className="empty-state">{config.setupCopy}</p>
        </section>
      )}
    </div>
  );
}
